import { useCallback, useRef } from 'react';
import { useKaiProApiClient } from '../useKaiProApiClient';

const GOOD_THRESHOLD_MS = 100;
const OKAY_THRESHOLD_MS = 1000;
const CACHE_TTL_MS = 30_000;

export type ConnectionQuality = 'good' | 'okay' | 'bad';

export type ServerReachabilityStatus = {
  reachable: boolean;
  responseTimeMs: number;
  quality: ConnectionQuality;
};

const getConnectionQuality = (responseTimeMs: number): ConnectionQuality =>
  responseTimeMs < GOOD_THRESHOLD_MS
    ? 'good'
    : responseTimeMs < OKAY_THRESHOLD_MS
    ? 'okay'
    : 'bad';

export const useServerReachabilityCheck = () => {
  const kaiProApiClient = useKaiProApiClient();
  const cacheRef = useRef<{ status: ServerReachabilityStatus; timestamp: number } | undefined>(undefined);
  const inflightRef = useRef<Promise<ServerReachabilityStatus> | undefined>(undefined);

  return useCallback(async (): Promise<ServerReachabilityStatus> => {
    if (cacheRef.current && Date.now() - cacheRef.current.timestamp < CACHE_TTL_MS) {
      return cacheRef.current.status;
    }

    if (inflightRef.current) return inflightRef.current;

    inflightRef.current = (async () => {
      try {
        const startTime = Date.now();
        await kaiProApiClient.getCurrentOrg();
        const responseTimeMs = Date.now() - startTime;
        return { reachable: true, responseTimeMs, quality: getConnectionQuality(responseTimeMs) } as const;
      } catch {
        return { reachable: false, responseTimeMs: 0, quality: 'bad' } as const;
      } finally {
        inflightRef.current = undefined;
      }
    })();

    const status = await inflightRef.current;
    cacheRef.current = { status, timestamp: Date.now() };
    return status;
  }, [kaiProApiClient]);
};
